import React, { createContext, useContext, useState, useEffect } from 'react';

export type ThemeMode = 'fueltron' | 'liquid-glass' | 'auto';

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  isLiquidGlass: boolean;
  isAuto: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('fueltron');

  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('fueltron_theme_mode') as ThemeMode;
    if (savedTheme && ['fueltron', 'liquid-glass', 'auto'].includes(savedTheme)) {
      setThemeModeState(savedTheme);
    }
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove all theme classes
    root.classList.remove('theme-fueltron', 'theme-liquid-glass');
    
    // Add appropriate theme class
    if (themeMode === 'liquid-glass') {
      root.classList.add('theme-liquid-glass');
      root.setAttribute('data-theme', 'liquid-glass');
    } else {
      root.classList.add('theme-fueltron');
      root.setAttribute('data-theme', 'fueltron');
    }
  }, [themeMode]);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem('fueltron_theme_mode', mode);
  };

  const toggleTheme = () => {
    const nextTheme: ThemeMode = themeMode === 'fueltron' ? 'liquid-glass' : 'fueltron';
    setThemeMode(nextTheme);
  };

  const isLiquidGlass = themeMode === 'liquid-glass';
  const isAuto = themeMode === 'auto';

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        setThemeMode,
        toggleTheme,
        isLiquidGlass,
        isAuto,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}; 